// File: src/hooks/transaction/useWithdrawTransaction.ts

import {
    useDialogPortal,
    useTransactionFees,
    useTransactionExecution,
    useGasEstimation,
    useProtocolStore,
} from "@/hooks";
import { withdrawEventEmitter, WITHDRAW_EVENTS } from "@/events";
import {
    gatewayVaultWithdrawTransaction,
    estimateVaultWithdrawTransactionGas,
    prepareVaultWithdrawTransaction,
} from "@/services";
import { formatUnits, parseUnits, type WalletClient } from "viem";
import { TOKEN_IDS, type TransactionHash } from "@/types";
import { useCallback, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { getGatewayContractAddress, QUERY_KEYS } from "@/config";
import Big from "big.js";
import { useAccount } from "wagmi";
import { useTokenApprovalV2 } from "./useTokenApprovalV2";

interface WithdrawTransactionProps {
    withdrawAmount: string; // This is the user-entered iUSD amount
}

export function useWithdrawTransaction({ withdrawAmount }: WithdrawTransactionProps) {
    const { closeDialog } = useDialogPortal();
    const { address } = useAccount();
    const { stats } = useProtocolStore();

    const bWithdrawAmount = Big(withdrawAmount).toFixed(18, 0);

    const exchangeRate = Big(`${stats.staked.exchangeRateNormalized}`).toFixed(18, 0);

    const siUSDWithdrawAmount: string | undefined = useMemo(() => {
        if (!exchangeRate || exchangeRate === "0") return "0";

        try {
            // Make exchange rate bigint, with 18 decimal precision
            const exchangeRateBig = parseUnits(exchangeRate.toString(), 18);

            // Parse withdraw amount to 18
            const withdrawAmountBig = parseUnits(bWithdrawAmount.toString(), 18);

            // Scale up numerator to preserve precision
            const scaledWithdrawAmount = withdrawAmountBig * BigInt(1e18);

            // Calculate raw value
            const raw = scaledWithdrawAmount / exchangeRateBig;

            const formattedToDecimal = formatUnits(raw, 18);

            return formattedToDecimal;
        } catch (error) {
            console.error("Error calculating siUSDWithdrawAmount:", error);
            return;
        }
    }, [bWithdrawAmount, exchangeRate]);

    const approvalAmount = siUSDWithdrawAmount ?? "0";

    const { isApproved, isApproving } = useTokenApprovalV2({
        tokenId: TOKEN_IDS.SIUSD,
        amount: approvalAmount,
        spender: getGatewayContractAddress(),
    });

    const estimateGasFn = useCallback(
        (walletClient: WalletClient) => {
            if (!siUSDWithdrawAmount) throw new Error("siUSDWithdrawAmount is not defined");
            return estimateVaultWithdrawTransactionGas(siUSDWithdrawAmount, walletClient);
        },
        [siUSDWithdrawAmount],
    );

    const prepareTransactionFn = useCallback(
        (walletClient: WalletClient) => {
            if (!siUSDWithdrawAmount) throw new Error("siUSDWithdrawAmount is not defined");
            return prepareVaultWithdrawTransaction(siUSDWithdrawAmount, walletClient);
        },
        [siUSDWithdrawAmount],
    );

    const {
        gasEstimate,
        isEstimatingGas,
        error: gasError,
    } = useGasEstimation({
        estimateGasFn,
        prepareTransactionFn,
        hasApproval: isApproved,
        tokenId: TOKEN_IDS.SIUSD,
    });

    const { gasPriceUSD, slippagePercent } = useTransactionFees({ estimatedGas: gasEstimate });

    const executeTransactionFn = useCallback(
        (walletClient: WalletClient) => {
            if (!siUSDWithdrawAmount) throw new Error("siUSDWithdrawAmount is not defined");
            return gatewayVaultWithdrawTransaction(siUSDWithdrawAmount, walletClient);
        },
        [siUSDWithdrawAmount],
    );

    const { invalidateQueries } = useQueryClient();
    const {
        transactionHash,
        isExecuting,
        error: transactionError,
        executeTransaction: executeWithdrawTransaction,
    } = useTransactionExecution({
        executeTransactionFn,
        onSuccess: (hash: TransactionHash) => {
            withdrawEventEmitter.emit(WITHDRAW_EVENTS.SUCCESS, {
                transactionHash: hash,
                withdrawAmount: bWithdrawAmount,
            });
            closeDialog();
            invalidateQueries({
                queryKey: QUERY_KEYS.TOKENS.BALANCE.LIST(address),
            });
        },
        errorMessage: "Failed to withdraw tokens",
    });

    return {
        withdrawAmount: bWithdrawAmount,
        siUSDWithdrawAmount,
        approvalAmount,
        gasPriceUSD,
        slippagePercent,
        transactionHash,
        executeWithdrawTransaction,
        isEstimatingGas,
        isLoading: isExecuting || isApproving,
        gasEstimate,
        needsApproval: !isApproved,
        error: gasError || transactionError,
        isExecuting,
    };
}
